// useConversations.jsx
// Global context that subscribes to the current user's conversations in real time.
// Provides: conversations list, total unread count, and createOrOpenConversation helper.
//
// Firestore schema — "conversations" collection:
//   participants:      string[]          (exactly 2 UIDs)
//   participantNames:  { [uid]: string } (display name snapshot)
//   postId:            string
//   postTitle:         string
//   postImg:           string
//   postRoute:         string            (in-app path to the source post — used by the header link)
//   lastMessage:       string            (truncated preview)
//   lastMessageAt:     Timestamp
//   unread:            { [uid]: number } (per-user unread count)
//   createdAt:         Timestamp
//
// Firestore schema — "conversations/{id}/messages" subcollection:
//   senderUid:   string
//   senderName:  string
//   kind:        "text" | "system" | "booking"   (default "text")
//   text:        string                          (text:1–1000, system:≤200, booking:any)
//   bookingId:   string                          (booking only — link to bookings/{id})
//   payload:     object                          (booking only — snapshot for display)
//   createdAt:   Timestamp

import React, { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  increment,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const ConversationsContext = createContext({
  conversations: [],
  totalUnread: 0,
  uid: null,
});

// Firestore rejects undefined values — strip them before writing card payloads
// so optional post/job fields (e.g. unset budget on a job) don't fail the write.
function omitUndefined(obj) {
  return Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => v !== undefined)
  );
}

export function ConversationsProvider({ children }) {
  const [uid, setUid] = useState(null);
  const [conversations, setConversations] = useState([]);

  // Mirror auth state
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
      if (!user) setConversations([]);
    });
  }, []);

  // Real-time subscription to conversations the user is part of
  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, "conversations"),
      where("participants", "array-contains", uid)
    );
    return onSnapshot(q, (snap) => {
      const convs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort(
          (a, b) =>
            (b.lastMessageAt?.toMillis() ?? 0) - (a.lastMessageAt?.toMillis() ?? 0)
        );
      setConversations(convs);
    });
  }, [uid]);

  const totalUnread = conversations.reduce(
    (acc, c) => acc + (c.unread?.[uid] ?? 0),
    0
  );

  return (
    <ConversationsContext.Provider value={{ conversations, totalUnread, uid }}>
      {children}
    </ConversationsContext.Provider>
  );
}

export function useConversations() {
  return useContext(ConversationsContext);
}

/**
 * Find an existing conversation between uid ↔ ownerUid about postId,
 * or create a new one.
 *
 * Returns { id, isNew } — callers can use isNew to post a one-time
 * listing card / first-contact card without duplicating it on follow-ups.
 */
export async function createOrOpenConversation({
  uid,
  userName,
  ownerUid,
  ownerName,
  postId,
  postTitle,
  postImg,
  postRoute,
}) {
  // Find existing conversation between these two users about this post
  const q = query(
    collection(db, "conversations"),
    where("participants", "array-contains", uid),
    where("postId", "==", postId)
  );
  const snap = await getDocs(q);
  const existing = snap.docs.find((d) =>
    d.data().participants.includes(ownerUid)
  );
  if (existing) {
    // Backfill postRoute on legacy conversations that pre-date this field.
    if (postRoute && !existing.data().postRoute) {
      try {
        await updateDoc(doc(db, "conversations", existing.id), { postRoute });
      } catch {
        /* non-fatal */
      }
    }
    return { id: existing.id, isNew: false };
  }

  // Create new conversation
  const ref = await addDoc(collection(db, "conversations"), {
    participants:     [uid, ownerUid],
    participantNames: { [uid]: userName || "User", [ownerUid]: ownerName || "Owner" },
    postId,
    postTitle: postTitle ?? "",
    postImg:   postImg ?? "",
    postRoute: postRoute ?? "",
    lastMessage:    "",
    lastMessageAt:  serverTimestamp(),
    unread:         { [uid]: 0, [ownerUid]: 0 },
    createdAt:      serverTimestamp(),
  });
  return { id: ref.id, isNew: true };
}

/**
 * Post a small system message (status pill) into a thread.
 * Does NOT update the conversation preview / unread — system pills are noise.
 */
export async function sendSystemMessage(convId, sender, text) {
  return addDoc(collection(db, "conversations", convId, "messages"), {
    senderUid:  sender.uid,
    senderName: sender.name || "User",
    kind:       "system",
    text:       String(text).slice(0, 200),
    createdAt:  serverTimestamp(),
  });
}

/**
 * Post a "listing card" into a thread so the recipient sees the post context
 * (image, title, type, price) inline. Sent on every Message owner / Message
 * seller button press — including repeat outreach about the same listing —
 * so each new exchange is anchored to its post. Booking flow has its own
 * card and does NOT use this.
 *
 * payload: { postId, postTitle, postImg, type, regularPrice }
 */
export async function sendListingCardMessage(convId, sender, otherUid, payload) {
  await addDoc(collection(db, "conversations", convId, "messages"), {
    senderUid:  sender.uid,
    senderName: sender.name || "User",
    kind:       "listing",
    postId:     payload?.postId || "",
    payload:    omitUndefined(payload),
    text:       "",
    createdAt:  serverTimestamp(),
  });

  await updateDoc(doc(db, "conversations", convId), {
    lastMessage:   "Sent the listing",
    lastMessageAt: serverTimestamp(),
    ...(otherUid ? { [`unread.${otherUid}`]: increment(1) } : {}),
  });
}

/**
 * Post a "job card" into a thread (the jobs counterpart to
 * sendListingCardMessage). Drops the job context — title, image, budget,
 * category — inline. Sent on every Message poster button press so each new
 * exchange is anchored to its job, even on repeat outreach.
 *
 * payload: { jobId, jobTitle, jobImg, category, budgetType, budgetMin, budgetMax }
 */
export async function sendJobCardMessage(convId, sender, otherUid, jobId, payload) {
  await addDoc(collection(db, "conversations", convId, "messages"), {
    senderUid:  sender.uid,
    senderName: sender.name || "User",
    kind:       "job",
    jobId,
    payload:    omitUndefined(payload),
    text:       "",
    createdAt:  serverTimestamp(),
  });

  await updateDoc(doc(db, "conversations", convId), {
    lastMessage:   "Sent the job",
    lastMessageAt: serverTimestamp(),
    ...(otherUid ? { [`unread.${otherUid}`]: increment(1) } : {}),
  });
}

/**
 * Post a booking-card message into a thread + bump the conversation preview /
 * recipient unread (so the inbox surfaces the request).
 *
 * payload should snapshot the booking display fields:
 *   { postId, postTitle, postImg, type, startDate, endDate, days, pricePerDay, totalPrice }
 */
export async function sendBookingCardMessage(convId, sender, otherUid, bookingId, payload) {
  await addDoc(collection(db, "conversations", convId, "messages"), {
    senderUid:  sender.uid,
    senderName: sender.name || "User",
    kind:       "booking",
    bookingId,
    payload:    omitUndefined(payload),
    text:       "",
    createdAt:  serverTimestamp(),
  });

  const previewDates =
    payload?.startDate && payload?.endDate
      ? `${payload.startDate} → ${payload.endDate}`
      : "Booking request";
  await updateDoc(doc(db, "conversations", convId), {
    lastMessage:   `Booking request: ${previewDates}`,
    lastMessageAt: serverTimestamp(),
    ...(otherUid ? { [`unread.${otherUid}`]: increment(1) } : {}),
  });
}
